import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { QueryParams } from './paginatedTable'

const INTERNAL_KEYS = ['search', 'page', 'sortBy', 'sortDirection', 'advancedSearch']

/**
 * Bridges PaginatedTable state to the browser URL.
 *
 * Reads initial values from searchParams and provides an `onParamsChange`
 * callback that keeps the URL in sync as PaginatedTable state changes.
 *
 * @param filterKeys - URL param names this table uses as filters
 *   (e.g. ['record_status_code', 'payment_method_id'])
 */
export function useTableUrlSync(filterKeys: string[] = [], advancedSearchKeys: string[] = []) {
    const [searchParams, setSearchParams] = useSearchParams()
    const managedKeys = [...INTERNAL_KEYS, ...filterKeys, ...advancedSearchKeys]

    const defaultSearch = searchParams.get('search') ?? ''
    const page = searchParams.get('page') ?? '1'
    const sortBy = searchParams.get('sortBy') ?? undefined
    const sortDirection = searchParams.get('sortDirection') ?? undefined

    const defaultFilters = useMemo(() => {
        const filters: Record<string, string> = {}
        for (const key of filterKeys) {
            const value = searchParams.get(key)
            if (value) {
                filters[key] = value
            }
        }
        return filters
    }, [filterKeys.join('|'), searchParams.toString()])

    const defaultAdvancedSearch = useMemo(() => {
        const advancedSearch: Record<string, string> = {}
        for (const key of advancedSearchKeys) {
            const value = searchParams.get(key)
            if (value) {
                advancedSearch[key] = value
            }
        }
        return advancedSearch
    }, [advancedSearchKeys.join('|'), searchParams.toString()])

    const persistSearch = useCallback(
        (value: string) => {
            const next = new URLSearchParams(searchParams)

            if (value) {
                next.set('search', value)
            } else {
                next.delete('search')
            }

            next.delete('page')

            if (next.toString() !== searchParams.toString()) {
                setSearchParams(next, { replace: true })
            }
        },
        [searchParams, setSearchParams]
    )

    const onParamsChange = useCallback(
        (params: QueryParams) => {
            const next = new URLSearchParams(searchParams)

            for (const key of managedKeys) {
                next.delete(key)
            }

            if (params.search) next.set('search', params.search)
            if (params.page && params.page !== '1') next.set('page', params.page)
            if (params.sortBy || sortBy) next.set('sortBy', params.sortBy ?? sortBy ?? '')
            if (params.sortDirection || sortDirection) {
                next.set('sortDirection', params.sortDirection ?? sortDirection ?? '')
            }

            for (const [key, value] of Object.entries(params)) {
                if (key === 'advancedSearch' && typeof value === 'object' && value !== null) {
                    for (const advancedSearchKey of advancedSearchKeys) {
                        const advancedSearchValue = value[advancedSearchKey]
                        if (advancedSearchValue != null && advancedSearchValue !== '') {
                            next.set(advancedSearchKey, String(advancedSearchValue))
                        }
                    }
                    continue
                }

                if (INTERNAL_KEYS.includes(key)) continue
                if (value != null && value !== '') {
                    next.set(key, String(value))
                }
            }

            if (next.toString() !== searchParams.toString()) {
                setSearchParams(next, { replace: true })
            }
        },
        [
            advancedSearchKeys.join('|'),
            managedKeys.join('|'),
            searchParams,
            setSearchParams,
            sortBy,
            sortDirection,
        ]
    )

    return {
        defaultSearch,
        defaultAdvancedSearch,
        defaultFilters,
        defaultPage: page,
        page,
        sortBy,
        sortDirection,
        onParamsChange,
        persistSearch,
    }
}
